/**
 * Payment-profile picker (S&S auto-send Phase A). S&S requires a PaymentProfile on a prepay (card/bank)
 * account — it rejects even a $0 `testOrder:true` request without one. This picks WHICH saved S&S method
 * an order references.
 *
 * The operator NEVER types a profile ID: they enter the S&S account email, we list the saved methods via
 * S&S, and they select one. A hand-typed ID could silently point at the wrong instrument.
 *
 * Admin / inventory-admin only — the server gates the reads too (a label like "…Bank 1234 (John Doe)" is
 * financial PII, unlike the ship-to address). No card data ever reaches this app; only a reference ID.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';

export function PaymentProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {open && <PaymentProfileBody onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function PaymentProfileBody({ onClose }: { onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;

  const query = useQuery({
    queryKey: ['payment-profile-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/payment-profile-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the payment profile.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  const cfg = query.data;
  useEffect(() => {
    if (!cfg?.body) return;
    setEmail(cfg.body.email);
    setSelected(cfg.body.profileID);
  }, [cfg]);

  // Explicit lookup (not on every keystroke) — each call hits S&S.
  const lookup = useMutation({
    mutationFn: async (lookupEmail: string) => {
      const { data, error } = await client.GET('/payment-profiles', {
        params: { query: { email: lookupEmail } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to look up payment profiles.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/payment-profile-config', {
        // Only the identifying pair — the server re-fetches from S&S and derives the display label, so a
        // stale label can never describe a different instrument than the one actually stored.
        body: { email: email.trim(), profileID: selected as number },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Save failed.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-profile-config'] });
      onClose();
    },
  });

  // Show the looked-up list ONLY while the email still matches the one it was fetched for. Editing the
  // email must invalidate the list and the selection — otherwise the UI keeps affirming a pairing that
  // was never looked up. (The server verifies the pair regardless; this just stops the misleading UI.)
  const lookedUpEmail = lookup.data?.email;
  const listIsCurrent = lookedUpEmail != null && lookedUpEmail === email.trim();
  const profiles = listIsCurrent ? (lookup.data?.profiles ?? []) : [];
  const canSave = canWrite && selected != null && profiles.some((p) => p.profileID === selected);

  return (
    <>
      <DialogHeader>
        <DialogTitle>S&amp;S payment method</DialogTitle>
        <DialogDescription>
          S&amp;S requires a saved payment method on your account before it will accept an order —
          including a $0 test order. Pick which one your orders reference.
        </DialogDescription>
      </DialogHeader>

      {query.isPending && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {query.isError && (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      )}

      {cfg && (
        <div className="space-y-3">
          {!canWrite && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              Read-only view. Changing the payment method requires the admin or inventory-admin
              group.
            </p>
          )}

          {cfg.body && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
              <span className="text-muted-foreground">Currently used: </span>
              <span className="font-medium">
                {cfg.body.label ?? `Profile ${cfg.body.profileID}`}
              </span>
              {cfg.body.profileType && (
                <span className="text-muted-foreground"> · {cfg.body.profileType}</span>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="pp-email" className="text-xs">
              S&amp;S account email
            </Label>
            <div className="flex gap-2">
              <Input
                id="pp-email"
                type="email"
                value={email}
                disabled={!canWrite}
                placeholder="you@example.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSelected(null); // a profile id is meaningless against a different account
                }}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={!canWrite || !email.trim() || lookup.isPending}
                onClick={() => lookup.mutate(email.trim())}
              >
                {lookup.isPending && <Loader2 className="size-4 animate-spin" />}
                Look up
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              The email on your ssactivewear.com account, where the card/bank is saved.
            </p>
          </div>

          {lookup.isError && (
            <p className="text-xs text-destructive">
              {lookup.error instanceof Error ? lookup.error.message : 'Lookup failed.'}
            </p>
          )}

          {lookup.isSuccess && !listIsCurrent && (
            <p className="text-[11px] text-muted-foreground">
              Email changed — look up again to see that account&apos;s saved methods.
            </p>
          )}

          {lookup.isSuccess && listIsCurrent && profiles.length === 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              No saved payment methods on that S&amp;S account. Add a card or bank account at
              ssactivewear.com, then look up again.
            </p>
          )}

          {profiles.length > 0 && (
            <fieldset className="space-y-1.5">
              <legend className="text-xs font-medium">Saved methods</legend>
              {profiles.map((p) => (
                <label
                  key={p.profileID}
                  className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
                >
                  <input
                    type="radio"
                    name="payment-profile"
                    checked={selected === p.profileID}
                    disabled={!canWrite}
                    onChange={() => setSelected(p.profileID)}
                  />
                  <CreditCard className="size-4 text-muted-foreground" />
                  <span className="flex-1">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{p.profileType}</span>
                </label>
              ))}
            </fieldset>
          )}

          {save.isError && (
            <p className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed.'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
              {save.isPending && <Loader2 className="size-4 animate-spin" />}
              Use this method
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
